import React from 'react';
import { X, Clock, User, FileText, MapPin, Phone, Mail, Edit, Trash2, Calendar } from 'lucide-react';
import { CalendarEvent } from '../types';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface AppointmentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  appointment: CalendarEvent | null;
}

const AppointmentDetailModal: React.FC<AppointmentDetailModalProps> = ({
  isOpen,
  onClose,
  onEdit,
  onDelete,
  appointment
}) => {
  if (!isOpen || !appointment) return null;

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy 'a las' HH:mm", { locale: es });
  };

  const formatTime = (dateString: string) => {
    return format(new Date(dateString), "HH:mm", { locale: es });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'Programada';
      case 'confirmed':
        return 'Confirmada';
      case 'in-progress':
        return 'En Progreso';
      case 'completed':
        return 'Completada';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Calendar className="h-6 w-6 text-dental-600" />
            <h3 className="text-xl font-semibold text-gray-900">
              Detalles de la Cita
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(appointment.status)}`}>
              {getStatusText(appointment.status)}
            </span>
            <div className="flex space-x-2">
              <button
                onClick={onEdit}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dental-500"
              >
                <Edit className="h-4 w-4 mr-1" />
                Editar
              </button>
              <button
                onClick={onDelete}
                className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Eliminar
              </button>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Fecha y Hora</p>
                  <p className="text-sm text-gray-600">{formatDateTime(appointment.start)}</p>
                  {appointment.end && (
                    <p className="text-sm text-gray-500">
                      Hasta las {formatTime(appointment.end)}
                    </p>
                  )}
                </div>
              </div>

              {/* Patient Info */}
              <div className="flex items-start space-x-3">
                <User className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Paciente</p>
                  <p className="text-sm text-gray-600">{appointment.patientName}</p>
                  {appointment.patientPhone && (
                    <div className="flex items-center space-x-1 mt-1">
                      <Phone className="h-3 w-3 text-gray-400" />
                      <p className="text-xs text-gray-500">{appointment.patientPhone}</p>
                    </div>
                  )}
                  {appointment.patientEmail && (
                    <div className="flex items-center space-x-1 mt-1">
                      <Mail className="h-3 w-3 text-gray-400" />
                      <p className="text-xs text-gray-500">{appointment.patientEmail}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Treatment */}
              {appointment.treatmentType && (
                <div className="flex items-start space-x-3">
                  <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Tratamiento</p>
                    <p className="text-sm text-gray-600">{appointment.treatmentType}</p>
                  </div>
                </div>
              )}

              {/* Doctor */}
              {appointment.dentist && (
                <div className="flex items-start space-x-3">
                  <User className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Doctor</p>
                    <p className="text-sm text-gray-600">{appointment.dentist}</p>
                  </div>
                </div>
              )}

              {/* Room */}
              {appointment.room && (
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Consultorio</p>
                    <p className="text-sm text-gray-600">{appointment.room}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {appointment.notes && (
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-start space-x-3">
                <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Notas</p>
                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{appointment.notes}</p>
                </div>
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div className="border-t border-gray-200 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-500">
              {appointment.createdAt && (
                <div>
                  <span className="font-medium">Creada:</span>{' '}
                  {format(new Date(appointment.createdAt), "dd/MM/yyyy HH:mm", { locale: es })}
                </div>
              )}
              {appointment.updatedAt && (
                <div>
                  <span className="font-medium">Última actualización:</span>{' '}
                  {format(new Date(appointment.updatedAt), "dd/MM/yyyy HH:mm", { locale: es })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dental-500"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetailModal;